// Speech service that handles both browser speech recognition and OpenAI Whisper
import axios from 'axios';
import { isIOS } from '../utils/deviceDetection';

const OPENAI_API_KEY = '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';
const OPENAI_WHISPER_URL = 'https://api.openai.com/v1/audio/transcriptions';

class SpeechService {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
    this.recognition = null;
  }

  // Check if device is iOS
  isIOSDevice() {
    return isIOS();
  }

  // Check if browser speech recognition is supported
  isBrowserSpeechRecognitionSupported() {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  // Check if we should use OpenAI Whisper (iOS devices)
  shouldUseWhisper() {
    return this.isIOSDevice();
  }

  // Check if speech recognition is supported at all
  isSpeechRecognitionSupported() {
    if (this.isIOSDevice()) {
      // For iOS, we'll use OpenAI Whisper with audio recording
      return this.isMediaRecorderSupported();
    }
    // For other platforms, use browser speech recognition
    return this.isBrowserSpeechRecognitionSupported();
  }

  // Check if MediaRecorder is supported for audio recording
  isMediaRecorderSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
  }

  // Browser speech recognition (for Android/other platforms)
  async startBrowserSpeechRecognition(onResult, onError, onStart, onEnd) {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();

    this.recognition.continuous = true; // Keep listening while button is held
    this.recognition.interimResults = false;
    this.recognition.lang = 'en-US';

    this.recognition.onstart = () => {
      onStart && onStart();
    };

    this.recognition.onresult = (event) => {
      const transcript = event.results[event.results.length - 1][0].transcript;
      if (transcript.trim()) {
        onResult && onResult(transcript);
      }
    };

    this.recognition.onerror = (event) => {
      onError && onError(event.error);
    };

    this.recognition.onend = () => {
      onEnd && onEnd();
    };

    this.recognition.start();
  }

  // Start audio recording for OpenAI Whisper (for iOS)
  async startAudioRecording(onResult, onError, onStart, onEnd) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });

      // Try different MIME types for better compatibility
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
        }
      }

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType
      });

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(this.audioChunks, { type: mimeType });
          const transcript = await this.transcribeWithWhisper(audioBlob);
          onResult && onResult(transcript);
        } catch (error) {
          console.error('Error transcribing audio:', error);
          onError && onError('transcription-failed');
        } finally {
          // Stop all tracks to release microphone
          stream.getTracks().forEach(track => track.stop());
          onEnd && onEnd();
        }
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      onStart && onStart();

    } catch (error) {
      console.error('Error starting audio recording:', error);
      onError && onError('audio-capture');
    }
  }

  // Stop audio recording
  stopAudioRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
  }

  // Transcribe audio using OpenAI Whisper API
  async transcribeWithWhisper(audioBlob) {
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');
    formData.append('model', 'whisper-1');
    formData.append('language', 'en');

    const response = await axios.post(OPENAI_WHISPER_URL, formData, {
      headers: {
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.text;
  }

  // Main method to start speech recognition
  async startSpeechRecognition(onResult, onError, onStart, onEnd) {
    if (this.shouldUseWhisper()) {
      // Use OpenAI Whisper for iOS
      await this.startAudioRecording(onResult, onError, onStart, onEnd);
    } else {
      // Use browser speech recognition for other platforms
      await this.startBrowserSpeechRecognition(onResult, onError, onStart, onEnd);
    }
  }

  // Stop speech recognition
  stopSpeechRecognition() {
    if (this.shouldUseWhisper()) {
      this.stopAudioRecording();
    } else {
      // For browser speech recognition, we need to stop it manually
      if (this.recognition) {
        this.recognition.stop();
      }
    }
  }

  // Get recording status (useful for UI feedback)
  getRecordingStatus() {
    return this.isRecording;
  }
}

export default new SpeechService();
